import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import RechargeBillingStateSelect from "../selects/RechargeBillingStateSelect";
import { rechargeBillingStatusSchema, RechargeBillingStatusSchemaInput, RechargeBillingStatusSchemaOutput } from "../../utils/recharge.schema";

import { UpdateRechargeBillingStatesDocument } from "__generated__/view/recharge/graphql/recharge.mutations.generated";

interface RechargeBillingStatusEditDialogProps {
	open: boolean;
	onClose: () => void;
	cargoActionIds: number[];
	refetch: () => void;
}

const RechargeBillingStatusEditDialog = ({ open, onClose, cargoActionIds, refetch }: RechargeBillingStatusEditDialogProps) => {

	const defaultValues = {
		billingState: null,
	};

	const { control, handleSubmit, reset } = useForm<RechargeBillingStatusSchemaInput, unknown, RechargeBillingStatusSchemaOutput>({
		resolver: zodResolver(rechargeBillingStatusSchema),
		defaultValues: defaultValues,
	});

	const [ updateRechargeBillingStates, { loading } ] = useMutation(UpdateRechargeBillingStatesDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: RechargeBillingStatusSchemaOutput) => {
		updateRechargeBillingStates({
			variables: {
				ids: cargoActionIds,
				billingState: data.billingState,
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open) {
			reset(defaultValues);
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "RECHARGE_BILLING_STATE" })}
			form='billingStatusForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="billingStatusForm">
				<FormGroup
					label={intl.formatMessage({ id: "RECHARGE_BILLING_STATE" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="billingState"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<RechargeBillingStateSelect { ...field } />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default RechargeBillingStatusEditDialog;
